/**
 * A Shelf is drawn at the size the thing it depicts actually is.
 *
 * Board space is millimeters, so a rack's width is its capacity times the
 * thickness of a jewel case and nothing else has to be kept in step with it.
 * That is what lets the board answer where a slot is by arithmetic rather than
 * by measuring the DOM — at any zoom, for racks that are off screen.
 *
 * These are the desktop's own numbers. They have to be, because a Shelf is one
 * arrangement seen from two applications: a rack that was 12 cases wide where
 * it was built and something else here would be a different rack.
 */

/** A standard jewel case, edge on. What one slot is wide. */
export const JEWEL_CASE_THICKNESS_MILLIMETERS = 10.4;

/** A standard jewel case, standing. What one slot is tall. */
export const JEWEL_CASE_HEIGHT_MILLIMETERS = 124.5;

/**
 * The printed spine inside that case - the folded flap of the Back artwork,
 * which is the only part of a project the reader of a rack ever sees. Narrower
 * than the case, because the plastic is not printed.
 */
export const SPINE_ARTWORK_WIDTH_MILLIMETERS = 6.5;

/** The Back artwork's own height, which the printed spine is as tall as. */
export const SPINE_ARTWORK_HEIGHT_MILLIMETERS = 118;

/**
 * The frame around the run of cases, the same on every side.
 *
 * Slim, and with no head room above the discs: at 16 mm a side the frame was
 * what the eye saw first and a rack lying on its side had a dead strip beside
 * every album. The cases are true to size; the furniture around them is the
 * part that gets to be small.
 */
export const SHELF_PANEL_THICKNESS_MILLIMETERS = 5;

/**
 * The name plate standing on top of a rack, upright in both orientations.
 *
 * Joined to the carcass rather than floating above it: with a gap between them
 * the name read as a second object.
 */
export const SHELF_HEADER_HEIGHT_MILLIMETERS = 14;

/**
 * How far the board zooms.
 *
 * A board of a hundred racks is a thing to stand back from, and the far end of
 * this range is where a whole collection fits on one screen.
 */
export const MINIMUM_BOARD_ZOOM_PERCENTAGE = 4;
export const MAXIMUM_BOARD_ZOOM_PERCENTAGE = 400;
export const DEFAULT_BOARD_ZOOM_PERCENTAGE = 60;

/**
 * Millimeters to CSS pixels at 100%, the same 96dpi the desktop's editor canvas
 * draws at, so a rack at 100% is the size a rack printed at 100% would be.
 */
export const BOARD_PIXELS_PER_MILLIMETER = 96 / 25.4;
